import isNullOrUndefined from "_shared/_utilities/is-null-or-undefined";
import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHDate from "_shared/dates/date";
import DateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import { DocumentClassificationStatus } from "documents/classification/document-classification-status";
import { FileScanStatus } from "messaging/attachments/file-info/file-scan-status";
import { MessageAttachmentContentAccess } from "messaging/attachments/file-info/message-attachment-content-access";
import { z } from "zod";

export type MessageFileInfoJSON = z.infer<typeof MessageFileInfoSchema.MessageFileInfoJSONZ>;
export type MessageFileInfoParams = z.infer<typeof MessageFileInfoSchema.MessageFileInfoZ>;

export default class MessageFileInfoSchema {
  public static readonly MessageFileInfoJSONZ = z.object({
    id: z.uuid(),
    name: z.string(),
    fileSize: z.number(),
    mimeType: z.string().nullish(),
    createdDate: DateSchema.DateJSONZ.nullish(),
    rawData: z.instanceof(Blob).nullish(),
    dataUri: z.string().nullish(),
    /** The forum the file was shared in. Sent on a list that spans forums; absent elsewhere. */
    forumId: z.uuid().nullish(),
    /** The message the file was sent with. Sent wherever `forumId` is. */
    messageId: z.uuid().nullish(),
    /** What the malware scan has said about the file. Absent on a file the client made itself. */
    scanStatus: z.enum(FileScanStatus).nullish(),
    /**
     * How far classification got (#6877), with the values a document's classification has. Absent
     * where the API did not read it, and for a file with no classification request.
     */
    classificationStatus: z.enum(DocumentClassificationStatus).nullish(),
    /** The subject matters the classifier found, by name. Empty unless classification succeeded. */
    subjectMatters: z.array(z.string()).nullish(),
    /** Whether the reader may have the file's content, by its subject matters (#6993). Absent where the API did not say. */
    contentAccess: z.enum(MessageAttachmentContentAccess).nullish(),
    /** The subject matters the reader is refused, by name, as a refusal names them (#6993). */
    restrictedSubjectMatters: z.array(z.string()).nullish(),
  });

  public static readonly MessageFileInfoZ = z.object({
    id: z.instanceof(Guid),
    name: z.string(),
    fileSize: z.number(),
    mimeType: z.string(),
    createdDate: DateSchema.type,
    rawData: z.instanceof(Blob).optional(),
    dataUri: z.string().optional(),
    forumId: z.instanceof(Guid).optional(),
    messageId: z.instanceof(Guid).optional(),
    scanStatus: z.enum(FileScanStatus).optional(),
    classificationStatus: z.enum(DocumentClassificationStatus).optional(),
    subjectMatters: z.array(z.string()).optional(),
    contentAccess: z.enum(MessageAttachmentContentAccess).optional(),
    restrictedSubjectMatters: z.array(z.string()).optional(),
  });

  public static validate(values: unknown): MessageFileInfoParams {
    return parseOrThrow(MessageFileInfoSchema.MessageFileInfoZ, values, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(json: unknown): MessageFileInfoJSON {
    return parseOrThrow(MessageFileInfoSchema.MessageFileInfoJSONZ, json, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "validateJSON",
      payloadSample: json,
    });
  }

  public static isValid(values: unknown): values is MessageFileInfoParams {
    try {
      MessageFileInfoSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(json: unknown): json is MessageFileInfoJSON {
    try {
      MessageFileInfoSchema.validateJSON(json);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(json: MessageFileInfoJSON): MessageFileInfoParams {
    if (isNullOrUndefined(json)) return json;
    const parsed = parseOrThrow(MessageFileInfoSchema.MessageFileInfoJSONZ, json, {
      logError: true,
      schemaName: "MessageFileInfoSchema",
      operation: "deserialize",
      payloadSample: json,
    });
    return MessageFileInfoSchema.codec.decode(parsed);
  }

  public static serialize(params: MessageFileInfoParams): MessageFileInfoJSON {
    if (isNullOrUndefined(params)) return params;
    const parsed = MessageFileInfoSchema.validate(params);
    if (isNullOrUndefined(parsed)) return parsed;
    return MessageFileInfoSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    MessageFileInfoSchema.MessageFileInfoJSONZ,
    MessageFileInfoSchema.MessageFileInfoZ,
    {
      decode: (data) => ({
        id: new Guid(GuidSchema.deserialize(data.id)),
        name: data.name,
        fileSize: data.fileSize ?? 0,
        mimeType: data.mimeType ?? "application/octet-stream",
        createdDate: new AHDate(DateSchema.deserialize(data.createdDate)),
        dataUri: data.dataUri ?? undefined,
        forumId: data.forumId ? new Guid(GuidSchema.deserialize(data.forumId)) : undefined,
        messageId: data.messageId ? new Guid(GuidSchema.deserialize(data.messageId)) : undefined,
        scanStatus: data.scanStatus ?? undefined,
        classificationStatus: data.classificationStatus ?? undefined,
        subjectMatters: data.subjectMatters ?? undefined,
        contentAccess: data.contentAccess ?? undefined,
        restrictedSubjectMatters: data.restrictedSubjectMatters ?? undefined,
      }),
      encode: (fileInfo) => ({
        id: fileInfo.id.value,
        name: fileInfo.name,
        fileSize: fileInfo.fileSize,
        mimeType: fileInfo.mimeType,
        createdDate: fileInfo.createdDate.toJSON(),
        dataUri: fileInfo.dataUri,
        forumId: fileInfo.forumId?.value,
        messageId: fileInfo.messageId?.value,
        scanStatus: fileInfo.scanStatus,
        classificationStatus: fileInfo.classificationStatus,
        subjectMatters: fileInfo.subjectMatters,
        contentAccess: fileInfo.contentAccess,
        restrictedSubjectMatters: fileInfo.restrictedSubjectMatters,
      }),
    }
  );
}
